// EN
export const propCreateHc = {
  firstCreate: {
    // Accommodation types
    typeApartmentLabel: `Apartment`,
    typeApartmentDesc: `A furnished, self-contained place rented out as a whole.`,
    typeVillaLabel: `Villa or house`,
    typeVillaDesc: `Houses, villas and chalets rented out entirely.`,
    typeHotelLabel: `Hotel, hostel or B&B`,
    typeHotelDesc: `Properties that manage several rooms.`,
    typeCampingLabel: `Campsite`,
    typeCampingDesc: `Campsites and pitches with several spots or units.`,

    // Venue / unit nouns
    venueHotel: `hotel`,
    venueCamping: `campsite`,
    roomHotel: `room`,
    roomCamping: `pitch`,
    roomHotelPlural: `rooms`,
    roomCampingPlural: `pitches`,
    yourVenue: `your {venue}`,

    // Common buttons
    back: `Back`,
    continue: `Continue`,

    // Step: type
    typeTitle: `Let's list your accommodation!`,
    typeSubtitle: `To start, choose the type of accommodation you want to publish.`,

    // Step: quantity
    quantityTitle: `How many accommodations do you want to list?`,
    quantitySubtitle: `You can start with one or create several at once.`,
    oneTitle: `1 accommodation`,
    oneDesc: `I just want to publish one for now.`,
    manyTitle: `More than 1 accommodation`,
    manyDesc: `I have several to list.`,

    // Step: location (same building or not)
    locationTitle: `Are they in the same building or address?`,
    locationSubtitle: `This helps us organize your accommodations in the best way.`,
    sameBuildingYesTitle: `Yes, in the same building or address`,
    sameBuildingYesDesc: `They will be grouped into a single listing with several units.`,
    sameBuildingNoTitle: `No, at different addresses`,
    sameBuildingNoDesc: `Each one will be an independent listing (you set it up afterwards).`,

    // Step: names (dynamic titles and subtitles)
    namesTitleAttach: `Add a unit to your listing`,
    namesTitleHotel: `What is your {venue} called?`,
    namesTitleGrouped: `Name each unit`,
    namesTitleList: `Name each accommodation`,
    namesTitleDefault: `What is your accommodation called?`,
    namesSubAttach: `It will be added as a new unit inside “{name}”.`,
    yourListing: `your listing`,
    namesSubHotel: `It's the name your guests will see. In the next step you add the {room} types.`,
    namesSubGrouped: `Each unit will have its own calendar within the same listing.`,
    namesSubList: `Each one will be an independent listing with its own settings.`,
    namesSubDefault: `It's the name your guests will see. You can change it whenever you want.`,
    addUnitLabel: `Add another unit`,
    addListingLabel: `Add another accommodation`,

    // Main listing card (grouped)
    mainListingEyebrow: `Main listing`,
    mainListingSub: `Groups all the units in this building into a single listing.`,
    mainListingNameLabel: `Name of the main accommodation`,
    mainListingNamePlaceholder: `E.g. Residencial Bilbao`,
    mainListingNameHint: `Building, tower or location that groups the units.`,
    currency: `Currency`,
    currencyHintUnit: `Nightly rate for each unit.`,

    // Unit cards
    unitNameLabel: `Unit name`,
    listingNameLabel: `Accommodation name`,
    phRoomCamping: `E.g. Pinar del Mar Campsite`,
    phRoomHotel: `E.g. Hotel Costa Azul`,
    phAttach: `E.g. Luxury studio at Playa Roca, seafront`,
    phGrouped: `E.g. Apartment {n}A`,
    phList: `Accommodation {n}`,
    phDefault: `E.g. Downtown Apartment`,
    remove: `Remove`,
    internalNameOptional: `Internal name (optional)`,
    pricePerNight: `Price / night`,
    currencyHintHotel: `All {rooms} use this currency.`,

    // Internal name (single-name case)
    internalNameLabel: `Internal name`,
    optional: `(optional)`,
    internalNamePlaceholderSingle: `E.g. Renovated apt 2024 / concierge keys`,
    internalNameHint: `Only you see it, to identify it internally. It is not shown to guests.`,

    // Step: room types (hotel/campsite)
    roomsTitle: `What {rooms} does {venue} have?`,
    roomsSubtitle: `Add each {room} type with its quantity and its price per night. You can edit it whenever you want.`,
    roomTypeLabel: `{room} type`,
    phRoomTypeCamping: `E.g. Pitch with electricity`,
    phRoomTypeHotel: `E.g. Deluxe double room`,
    quantity: `Quantity`,
    addRoomType: `Add {room} type`,

    // Step: address
    addressTitle: `Where is your accommodation?`,
    addressSubtitle: `Start typing and choose the address. We use it for the map and check-in.`,
    country: `Country`,
    countryHint: `We will search for addresses only within this country.`,
    address: `Address`,
    addressPlaceholder: `Street, number, city…`,
    addressPlaceholderNoCountry: `First choose the country`,
    city: `City`,
    zipCode: `Postal code`,

    // Step: rate
    priceTitle: `Your standard nightly rate`,
    priceSubtitle: `It's your base price. You can then adjust it by season or by channel.`,
    priceLabel: `Price per night`,
    perNight: `/ night`,
    priceHint: `You can change it whenever you want from the panel.`,

    // Primary button and creation reason
    createUnit: `Add unit`,
    createVenue: `Create my {venue}`,
    createListingsPlural: `Create my accommodation | Create my {count} accommodations`,
    reasonUnit: `unit`,
    reasonListing: `listing`,

    // Toasts / validation
    toastCompleteToContinue: `Fill in the details to continue.`,
    toastCompleteNames: `Fill in the name of each accommodation.`,
    toastCompleteLocation: `Fill in the location.`,
    toastCompleteRooms: `Complete each room: name, quantity and price per night.`,
    toastRoomRatePerUnit: `Enter the nightly rate for each unit.`,
    toastCompleteRate: `Fill in the nightly rate.`,
    toastCreatedPlural: `Accommodation created! | Accommodations created!`,
    toastCreateError: `We couldn't create the accommodation. Please try again.`,
  },

  welcome: {
    // Header titles
    arrivalTitleCaps: `Arrival Instructions`,
    arrivalTitle: `Arrival instructions`,
    arrivalSubtitle: `Message the guest receives on the day of their arrival.`,
    welcomeTitle: `Welcome message`,
    welcomeSubtitle: `Message the guest receives after completing their check-in.`,
    loadingMessages: `Loading messages...`,

    // Room-level welcome
    roomWelcomeTitle: `Welcome message for this room`,
    roomWelcomeSub: `If you leave it empty, the hotel's general welcome message is sent. The switch, attachments and OTA are configured at the hotel level.`,

    // Welcome switch
    welcomeEnableTitle: `Send welcome message`,
    welcomeEnableSub: `The guest receives it after completing their check-in. If you turn it off, it won't be sent.`,

    // Clone
    clone: `Clone`,
    clonePullTitle: `Bring settings from another accommodation`,
    clonePullSub: `Import the welcome from another one to this`,
    clonePushTitle: `Copy this configuration to another accommodation`,
    clonePushSub: `Apply this welcome to others`,
    cloneModalPullTitle: `Bring welcome settings`,
    cloneModalPushTitle: `Copy welcome message`,
    cloneModalPullSubtitle: `Choose the accommodation you want to bring the settings from. It will be applied to this listing.`,
    cloneModalPushSubtitle: `Copy this configuration (texts, mode, OTA and attachments) to the accommodations you choose.`,
    itemNounWelcome: `the welcome message`,
    arrivalTitleNoun: `arrival instructions`,
    itemNounArrival: `the arrival instructions`,

    // Configuration mode
    modeConfigLabel: `Configuration mode`,
    modeSingleTitle: `Single message`,
    modeSingleDesc: `All units share the same message`,
    modeActive: `Active`,
    modePropertyTitle: `Per property`,
    modePropertyDesc: `Each unit has its own message`,
    modePropertyNotice: `Individual mode active. Edit the message from each property.`,

    // Arrival switch
    arrivalEnableTitle: `Send arrival instructions`,
    arrivalAutoSend: `They are sent automatically on the day of arrival{atTime}.`,
    arrivalAtTime: ` at {time}`,

    // Channels / sending
    deliveryChannels: `Sending channels`,
    channelEmail: `Email`,
    sendAlsoOta: `Also send via OTA`,
    sendTime: `Send time`,

    // Editor
    editorSubtitle: `Set it up by language. Tip: use emojis and short text.`,
    translateTitle: `Translate from {lang} to the other languages`,
    translatingLangLabel: `Translating {lang}…`,
    translatingGeneric: `Translating…`,
    translate: `Translate`,

    // Attachments
    uploading: `Uploading…`,
    attach: `Attach`,
    emojis: `Emojis`,
    variables: `Variables`,
    variablesHint: `Click to insert them. They are replaced with the real booking data when sent.`,
    uploadDone: `Sent. Saving it on the server…`,
    uploadSizeProgress: `{done} of {total} MB`,
    attachHint: `Images and PDF up to {fileMb} MB · MP4 videos up to {videoMb} MB.`,
    whatsappLimits: `These are the limits WhatsApp allows.`,
    openFile: `Open {name}`,
    attachmentFallback: `attachment`,
    delete: `Delete`,
    open: `Open`,
    arrivalAttachments: `Arrival instructions attachments`,
    welcomeAttachments: `Welcome message attachments`,
    attachHintFull: `Images and PDF up to {fileMb} MB · MP4 videos up to {videoMb} MB. These are the limits WhatsApp allows.`,
    noAttachments: `No attachments yet.`,
    editorSubtitleProperty: `Set it up by language and attach files (images, PDF, video).`,

    // Save
    saveData: `Save data`,
    saving: `Saving…`,

    // Lock summary
    locksConfigured: `Locks configured`,
    accessMethodConfigured: `Access method configured`,
    guestAccess: `Guest access`,
    accessDisabledHtml: `Sending access codes is <strong>disabled</strong>, it will not be included in the arrival instructions.`,
    noAccessMethod: `There is no access method configured for this accommodation yet.`,
    locksHint: `It is configured in the "Locks and access" card of the listing.`,

    // Preview
    online: `online`,
    previewDayArrival: `Arrival day`,
    previewAfterRegister: `After check-in`,
    previewPlaceholder: `Write the message to see the preview here…`,
    previewIncludesAccess: `Includes the configured access`,
    languageLabel: `{lang} language`,
    previewFallbackName: `Your accommodation`,
    previewFallbackAccommodation: `your accommodation`,

    // Lock summaries (computed)
    lockNoteDynamic: `A new key is generated automatically for each booking`,
    lockTagBox: `Key box code`,
    lockTag: `Code`,
    lockCodePending: `Code pending configuration`,
    lockCodeValue: `{tag}: {code}`,
    lockLabelDefault: `Entrance`,

    // Access preview messages (computed)
    lockMsgDynamic: `🔒🔑 *{label} · {name}*\n\nYour access code is: *58117*\n\nValid during your stay.`,
    lockMsgBox: `🔑 *{label} · {name}*\n\nThe key box opens with the code: *{code}*\n\nInside you will find the keys to the accommodation. Please leave them back when you leave.`,
    lockMsgStatic: `🔒🔑 *{label} · {name}*\n\nThe lock code is: *{code}*\n\nUse it to open the accommodation door.`,

    // Variable labels (computed)
    varGuestName: `Guest name`,
    varFullName: `Full name`,
    varCheckinDate: `Check-in date`,
    varCheckoutDate: `Check-out date`,
    varCheckinTime: `Check-in time`,
    varCheckoutTime: `Check-out time`,
    varNights: `Number of nights`,
    varGuests: `Number of guests`,

    // Lock options (computed)
    lockOptBoxTitle: `Key box`,
    lockOptBoxDesc: `The keys are in a security key box.`,
    lockOptDynamicTitle: `Smart lock`,
    lockOptDynamicDesc: `Each booking creates a new key automatically.`,
    lockOptStaticTitle: `Lock with fixed code`,
    lockOptStaticDesc: `A key box or a lock with a fixed code.`,

    // Default arrival message (computed)
    defaultArrivalMessage: `🏡 *Arrival instructions*\n\nHi *{guest}* 👋\n\nWhen you arrive, head to the main entrance of the accommodation. 🔑\n\nIf you need help, write to us on this same WhatsApp 📲\n\nEnjoy your stay! ✨`,

    // Property context (inherited)
    inheritedChannelsTitle: `Channels inherited from the listing`,
    inheritedListingPrefix: `This property uses the centralized welcome of the listing`,
    emailChannel: `Email`,
    otaActive: `OTA active`,
    inheritedNote: `If you want to change the OTA sending of this welcome, do it from the listing settings.`,
    propertyDeliveryTitle: `How will this welcome be sent?`,
    propertyDeliverySubtitle: `Configure the sending channels for this property.`,
    propertyDeliveryNote: `These channels remain active by default. Here you can also add OTA sending for this property.`,
    otaRowTitle: `Send welcome messages via OTAs`,
    otaRowText: `It will try to send the welcome of this property via Booking.com and Airbnb using Channex, with its configured attachments.`,
    otaRowHint: `It is saved together with the welcome message and attachments.`,

    // Toasts / validation
    toastLoadError: `The messages could not be loaded.`,
    toastOtaUpdated: `OTA channel updated successfully`,
    toastOtaError: `The OTA sending could not be updated`,
    toastSendTimeUpdated: `Send time updated`,
    toastSendTimeError: `The time could not be saved`,
    toastArrivalEnabled: `Arrival instructions enabled`,
    toastArrivalDisabled: `Arrival instructions disabled`,
    toastStatusError: `The status could not be updated`,
    toastWelcomeEnabled: `Welcome message enabled`,
    toastWelcomeDisabled: `Welcome message disabled`,
    toastModeUpdated: `Mode updated successfully`,
    toastModeError: `The mode could not be updated`,
    toastAttachUploaded: `Attachment(s) uploaded successfully`,
    toastAttachUploadError: `Error uploading attachments`,
    toastAttachDeleted: `Attachment deleted`,
    toastAttachDeleteError: `The attachment could not be deleted`,
    toastChangesSaved: `Changes saved`,
    toastTranslateEmpty: `Write a message in the active language before translating.`,
    toastTranslateSuccess: `{count} languages translated! Review each tab before saving.`,
    toastTranslatePartial: `{success} translated, {failed} failed. You can try again.`,
    toastTranslateNone: `No language could be translated. Please try again.`,

    // File validation errors (WhatsApp)
    fileErrorVideoFormat: `❌ {name} is a {ext} video and WhatsApp only supports MP4. Convert it to MP4 and upload it again.`,
    fileErrorTooBig: `❌ {name} is {mb} MB and the maximum is {limMb} MB. `,
    fileErrorTooBigVideo: `WhatsApp does not allow larger videos: compress or trim it.`,
    fileErrorTooBigFile: `WhatsApp does not allow larger files.`,
  },
};
